import React, { useEffect, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';
import { apifetch } from '../services/interceptors';
import { downloadRecoveryCodes } from '../utils/downloadRecoveryCodes';

// Self-service two-factor settings: choose emailed code vs authenticator app (TOTP),
// enroll via QR (Google/Microsoft Authenticator, Duo), and view one-time recovery codes.
const TwoFactorSettings = () => {
  const [status, setStatus] = useState({ method: 'email', totp_enabled: false });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  // Enrollment state
  const [setup, setSetup] = useState(null); // { qr, secret, otpauthUrl }
  const [confirmCode, setConfirmCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState(null);

  // Disable state
  const [disablePassword, setDisablePassword] = useState('');
  const [showDisable, setShowDisable] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };
  };

  const loadStatus = async () => {
    setLoading(true);
    try {
      const res = await apifetch('s1/api/mfa/status', { headers: authHeaders() });
      const data = await res.json();
      if (res.ok) setStatus(data);
    } catch (e) {
      // non-fatal
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadStatus(); }, []);

  const beginSetup = async () => {
    setBusy(true); setError(''); setMessage(''); setRecoveryCodes(null);
    try {
      const res = await apifetch('s1/api/mfa/totp/setup', { method: 'POST', headers: authHeaders() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start setup');
      setSetup(data);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const confirmSetup = async () => {
    setBusy(true); setError(''); setMessage('');
    try {
      const res = await apifetch('s1/api/mfa/totp/verify-setup', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ code: confirmCode.trim() })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to verify code');
      setRecoveryCodes(data.recoveryCodes || []);
      setSetup(null);
      setConfirmCode('');
      setMessage('Authenticator enabled. Save your recovery codes below.');
      await loadStatus();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const switchMethod = async (method) => {
    setBusy(true); setError(''); setMessage('');
    try {
      const res = await apifetch('s1/api/mfa/method', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ method })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to update method');
      setMessage(`Login method set to ${method === 'totp' ? 'authenticator app' : 'emailed code'}.`);
      await loadStatus();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const disableTotp = async () => {
    setBusy(true); setError(''); setMessage('');
    try {
      const res = await apifetch('s1/api/mfa/totp/disable', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ password: disablePassword })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to disable');
      setMessage('Authenticator disabled. Emailed codes will be used.');
      setShowDisable(false);
      setDisablePassword('');
      setRecoveryCodes(null);
      await loadStatus();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (loading) return <div className="py-3"><Spinner animation="border" size="sm" /> Loading two-factor settings…</div>;

  return (
    <div className="mt-4">
      <h5 className="mb-3 border-bottom pb-2">Two-Factor Authentication</h5>

      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}
      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}

      <p className="text-muted small mb-3">
        Choose how you verify logins. An authenticator app (Google Authenticator, Microsoft Authenticator, Duo)
        generates codes instantly on your phone — no waiting for an email.
      </p>

      {/* Method chooser */}
      <div className="mb-3">
        <Form.Check
          type="radio"
          id="mfa-email"
          name="mfa-method"
          label="Emailed code (default)"
          checked={status.method === 'email'}
          disabled={busy}
          onChange={() => switchMethod('email')}
        />
        <Form.Check
          type="radio"
          id="mfa-totp"
          name="mfa-method"
          label={`Authenticator app${status.totp_enabled ? '' : ' (set up required)'}`}
          checked={status.method === 'totp'}
          disabled={busy || !status.totp_enabled}
          onChange={() => switchMethod('totp')}
        />
      </div>

      {/* Enroll / manage authenticator */}
      {!status.totp_enabled && !setup && (
        <Button variant="outline-primary" onClick={beginSetup} disabled={busy}>
          Set up authenticator app
        </Button>
      )}

      {setup && (
        <div className="p-3 border rounded mb-3">
          <p className="mb-2">1. Scan this QR code with your authenticator app:</p>
          <img src={setup.qr} alt="Authenticator QR code" style={{ width: 180, height: 180 }} />
          <p className="small text-muted mt-2 mb-2">
            Can’t scan? Enter this key manually: <code>{setup.secret}</code>
          </p>
          <Form.Group className="mb-2">
            <Form.Label>2. Enter the 6-digit code from the app to confirm</Form.Label>
            <Form.Control
              value={confirmCode}
              onChange={(e) => setConfirmCode(e.target.value)}
              placeholder="123456"
              style={{ maxWidth: 200 }}
            />
          </Form.Group>
          <Button onClick={confirmSetup} disabled={busy || !confirmCode.trim()}>
            {busy ? <Spinner animation="border" size="sm" /> : 'Confirm & enable'}
          </Button>
        </div>
      )}

      {recoveryCodes && (
        <Alert variant="warning" className="mt-3">
          <strong>Save your recovery codes.</strong> Each can be used once to sign in if you lose your device.
          They won’t be shown again.
          <pre className="mt-2 mb-2" style={{ fontFamily: 'monospace' }}>{recoveryCodes.join('\n')}</pre>
          <Button
            variant="outline-dark"
            size="sm"
            onClick={() => downloadRecoveryCodes(recoveryCodes)}
          >
            ⬇ Download codes (.txt)
          </Button>
        </Alert>
      )}

      {status.totp_enabled && (
        <div className="mt-3">
          {!showDisable ? (
            <Button variant="outline-danger" size="sm" onClick={() => setShowDisable(true)} disabled={busy}>
              Disable authenticator
            </Button>
          ) : (
            <div className="p-3 border rounded" style={{ maxWidth: 360 }}>
              <Form.Group className="mb-2">
                <Form.Label>Confirm your password to disable</Form.Label>
                <Form.Control
                  type="password"
                  value={disablePassword}
                  onChange={(e) => setDisablePassword(e.target.value)}
                />
              </Form.Group>
              <Button variant="danger" size="sm" className="me-2" onClick={disableTotp} disabled={busy || !disablePassword}>
                Disable
              </Button>
              <Button variant="secondary" size="sm" onClick={() => { setShowDisable(false); setDisablePassword(''); }}>
                Cancel
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TwoFactorSettings;
